import {
  Button,
  Card,
  Flex,
  Input,
  InputRef,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { ArrowsAltOutlined } from "@ant-design/icons";
import TourOverlay from "../Tour/TourOverlay";
import "./index.scss";

interface JobShareSectionProps {
  jobLink: string;
  jobTitle?: string;
  themeColor?: string;
  hasActiveJobPost?: boolean;
  inputRef: React.RefObject<InputRef>;
  onNavigateTo: () => void;
  onCopy: () => void;
  onPlatformClick: (platform: string) => void;
}

// Social share URL generators
const getSocialShareUrl = (
  platform: string,
  jobLink: string,
  jobTitle: string = "Job Opening"
) => {
  const encodedUrl = encodeURIComponent(jobLink);
  const encodedText = encodeURIComponent(
    `We're hiring! Check out this ${jobTitle} opportunity:`
  );
  const encodedFullText = encodeURIComponent(
    `We're hiring! Check out this ${jobTitle} opportunity: ${jobLink}`
  );

  switch (platform.toLowerCase()) {
    case "linkedin":
      // Use LinkedIn's share URL with text parameter for better sharing experience
      return `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodeURIComponent(jobTitle)}&summary=${encodedText}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
    case "whatsapp":
      return `https://api.whatsapp.com/send?text=${encodedFullText}`;
    default:
      return null;
  }
};

// Generate mailto link for email sharing
const getEmailShareUrl = (jobLink: string, jobTitle: string = "Job Opening") => {
  const subject = encodeURIComponent(`Job Opening: ${jobTitle}`);
  const body = encodeURIComponent(
    `Hi,\n\nWe're hiring! Check out this exciting ${jobTitle} opportunity.\n\nApply here: ${jobLink}\n\nLooking forward to your application!`
  );
  return `mailto:?subject=${subject}&body=${body}`;
};

const JobShareSection: React.FC<JobShareSectionProps> = ({
  jobLink,
  jobTitle = "Job Opening",
  onNavigateTo,
  onCopy,
  onPlatformClick,
  hasActiveJobPost,
  themeColor,
}) => {
  // Handle direct social share
  const handleSocialShare = (platform: string) => {
    const shareUrl = getSocialShareUrl(platform, jobLink, jobTitle);

    if (shareUrl) {
      // Open share URL in a popup window
      const width = 600;
      const height = 500;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;

      window.open(
        shareUrl,
        `Share on ${platform}`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );
    } else {
      // For platforms without direct share, use existing drawer
      onPlatformClick(platform);
    }
  };

  // Handle email sharing via mailto
  const handleEmailShare = () => {
    const mailtoUrl = getEmailShareUrl(jobLink, jobTitle);
    window.location.href = mailtoUrl;
  };

  return (
    <TourOverlay targetId="job-share-section" className="job-share-section">
      <Card
        className=""
        size="small"
        style={{ opacity: hasActiveJobPost ? 1 : 0.5, cursor: hasActiveJobPost ? "pointer" : "not-allowed", backgroundColor: themeColor }}
      >
        <div className="job-share-section-wrapper">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Share Job Post
          </Typography.Title>
          <Typography.Text>
            Share this link with prospective candidates to allow them to view and
            apply for job openings.
          </Typography.Text>

          <Flex gap={8} align="center" justify="space-between">
            <Space.Compact style={{ width: "100%" }}>
              <Input value={jobLink} readOnly={true} />
              <Button type="primary" onClick={onCopy} style={{ width: "100px" }} disabled={!hasActiveJobPost}>
                Copy
              </Button>
            </Space.Compact>
            <Tooltip title="Open in new tab">
              <Button type="default" onClick={onNavigateTo} disabled={!hasActiveJobPost}>
                <ArrowsAltOutlined />
              </Button>
            </Tooltip>
          </Flex>

          <Typography.Title level={5} style={{ margin: 0 }}>
            Share across multiple platforms
          </Typography.Title>

          <div className="job-share-section-social-buttons">
            {/* Direct Share Platforms */}
            <Tooltip title="Share on LinkedIn">
              <Button
                type="default"
                className="social-share-btn"
                onClick={() => handleSocialShare("LinkedIn")}
                disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/LinkedIN.svg"
                  alt="LinkedIn"
                />
                <span>LinkedIn</span>
              </Button>
            </Tooltip>

            <Tooltip title="Share on Facebook">
              <Button
                type="default"
                className="social-share-btn"
                onClick={() => handleSocialShare("Facebook")}
                disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/Facebook.svg"
                  alt="Facebook"
                />
                <span>Facebook</span>
              </Button>
            </Tooltip>

            <Tooltip title="Share on X (Twitter)">
              <Button
                type="default"
                className="social-share-btn"
                onClick={() => handleSocialShare("Twitter")}
                disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/Twitter.svg"
                  alt="Twitter"
                />
                <span>X</span>
              </Button>
            </Tooltip>

            <Tooltip title="Share on WhatsApp">
              <Button
                type="default"
                className="social-share-btn"
                onClick={() => handleSocialShare("WhatsApp")}
                disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/WhatsApp.svg"
                  alt="WhatsApp"
                />
                <span>WhatsApp</span>
              </Button>
            </Tooltip>

            {/* Naukri - needs drawer with instructions */}
            <Tooltip title="Share on Naukri">
              <Button
                type="default"
                className="social-share-btn"
                onClick={() => onPlatformClick("Naukri")}
                disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/Naukri.svg"
                  alt="Naukri"
                />
                <span>Naukri</span>
              </Button>
            </Tooltip>

            {/* Email - opens system email app */}
            <Tooltip title="Share via Email">
              <Button
                type="default"
                className="social-share-btn"
                onClick={handleEmailShare}
                 disabled={!hasActiveJobPost}
              >
                <img
                  src="/icons/JobsEmail.svg"
                  alt="Email"
                />
                <span>Email</span>
              </Button>
            </Tooltip>
          </div>
        </div>
      </Card>
    </TourOverlay>
  );
};

export default JobShareSection;
